import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Info, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useSubscription } from '@/contexts/useSubscription';
import { getPrices, createCheckout } from '@/lib/api/subscription';
import { getSubscriptionError } from '@/lib/errors';
import { captureFriendlyError } from '@/lib/sentry';
import { track } from '@/lib/analytics';
import OnboardingShell from '@/components/layout/OnboardingShell';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnDarkButton from '@/components/pricing/OnDarkButton';
import Spinner from '@/components/ui/Spinner';
import { PREMIUM_FEATURES } from '@/components/pricing/premiumFeatures';
import type { FriendlyError } from '@/lib/errors';
import type { ApiError } from '@/lib/api/client';
import type {
  OnboardingDiscount,
  PriceCadence,
  PriceInfo,
  PricesResponse,
  YearlyPriceInfo,
} from '@/types/database';

function discountIsTemporary(d: OnboardingDiscount): boolean {
  return d.duration !== 'forever';
}

function monthlyPeriodLabel(d: OnboardingDiscount | undefined): string {
  if (d && d.duration === 'repeating' && d.duration_in_months) {
    const n = d.duration_in_months;
    return `for ${n} ${n === 1 ? 'month' : 'months'}`;
  }
  return 'per month';
}

export default function SoftPaywallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isPremium } = useSubscription();

  const wasCanceled = searchParams.get('canceled') === '1';

  const [prices, setPrices] = useState<PricesResponse | null>(null);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [checkoutLoading, setCheckoutLoading] =
    useState<PriceCadence | null>(null);
  const [freeLoading, setFreeLoading] = useState(false);

  useEffect(() => {
    track('pricing_viewed', { source: 'onboarding' });
  }, []);

  useEffect(() => {
    let active = true;
    getPrices()
      .then((data) => {
        if (active) setPrices(data);
      })
      .catch((err) => {
        if (!active) return;
        const apiError = err as ApiError;
        const friendly = getSubscriptionError(apiError.code || 'prices_load_failed');
        captureFriendlyError(err, friendly);
        setError(friendly);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleCheckout = async (cadence: PriceCadence) => {
    if (!prices || !user?.id) return;
    const priceInfo = cadence === 'annual' ? prices.yearly : prices.monthly;

    setCheckoutLoading(cadence);
    setError(null);

    track('checkout_started', {
      plan: cadence,
      has_onboarding_discount: !!priceInfo.onboarding_discount,
    });

    try {
      const origin = window.location.origin;
      const { checkout_url } = await createCheckout({
        priceId: priceInfo.id,
        successUrl: `${origin}/onboarding/welcome?from=premium&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/onboarding/soft-paywall?canceled=1`,
        onboardingOffer: true,
      });
      track('onboarding_step_completed', { step: 'soft_paywall' });
      window.location.href = checkout_url;
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getSubscriptionError(apiError.code || 'checkout_failed');
      captureFriendlyError(err, friendly);
      setError(friendly);
      setCheckoutLoading(null);
    }
  };

  const handleStartFree = () => {
    if (!user?.id) return;
    setFreeLoading(true);
    track('onboarding_step_completed', { step: 'soft_paywall' });
    navigate('/onboarding/welcome?from=free', { replace: true });
  };

  const annualDiscount = prices?.yearly.onboarding_discount;
  const savingsLabel = annualDiscount?.discount_display?.replace(/\.00$/, '');

  const heroTitle = (
    <span className="block font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em] text-text-primary">
      {savingsLabel ? (
        <>
          Save{' '}
          <span className="relative inline-block italic">
            {savingsLabel}
            <span
              aria-hidden
              className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-primary/70"
            />
          </span>{' '}
          on your first year of Premium.
        </>
      ) : (
        <>
          <span className="italic">Onboarding</span> pricing,
          <br /> just for finishing setup.
        </>
      )}
    </span>
  );

  return (
    <OnboardingShell step={7} eyebrow="Onboarding offer" title={heroTitle}>
      {wasCanceled && !error && (
        <div className="rounded-xl bg-primary/5 border border-primary/15 px-3.5 py-3 flex gap-2.5 mb-6">
          <Info
            size={16}
            className="text-primary flex-shrink-0 mt-0.5"
            aria-hidden
          />
          <div className="flex-1 min-w-0 text-left">
            <p className="font-display font-semibold text-sm text-text-primary leading-tight">
              No charge made
            </p>
            <p className="font-sans text-[13px] text-text-secondary mt-1 leading-snug">
              Take another look, or continue with the free plan below.
            </p>
          </div>
        </div>
      )}

      {error && <ErrorAlert error={error} className="mb-6" />}

      {!prices && !error && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {prices && (
        <div className="mt-6 md:mt-10">
          <AnnualCard
            yearly={prices.yearly}
            isPremium={isPremium}
            loading={checkoutLoading === 'annual'}
            otherLoading={checkoutLoading === 'monthly'}
            onSelect={() => handleCheckout('annual')}
          />

          <MonthlyCard
            monthly={prices.monthly}
            isPremium={isPremium}
            loading={checkoutLoading === 'monthly'}
            otherLoading={checkoutLoading === 'annual'}
            onSelect={() => handleCheckout('monthly')}
          />

          <button
            type="button"
            onClick={handleStartFree}
            disabled={freeLoading || checkoutLoading !== null}
            className="block w-full text-center py-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <span className="block font-sans text-sm text-text-secondary group-hover:text-text-primary underline underline-offset-4 transition-colors">
              {freeLoading ? 'Setting up...' : 'Continue with free plan'}
            </span>
            {!freeLoading && (
              <span className="block font-sans text-xs text-text-muted mt-1">
                You'll forfeit this onboarding price
              </span>
            )}
          </button>

          <div className="flex items-center justify-center gap-1.5 font-sans text-xs text-text-muted mt-1">
            <ShieldCheck size={13} className="flex-shrink-0" aria-hidden />
            <span>Secure checkout · Cancel anytime · No surprise charges</span>
          </div>
        </div>
      )}
    </OnboardingShell>
  );
}

interface AnnualCardProps {
  yearly: YearlyPriceInfo;
  isPremium: boolean;
  loading: boolean;
  otherLoading: boolean;
  onSelect: () => void;
}

function AnnualCard({
  yearly,
  isPremium,
  loading,
  otherLoading,
  onSelect,
}: AnnualCardProps) {
  const discount = yearly.onboarding_discount;
  const heroMonthly = discount?.monthly_equivalent ?? yearly.monthly_equivalent;
  const yearTotal = discount?.display_price ?? yearly.display_price;
  const ribbonText = discount
    ? `SAVE ${discount.discount_display}`
    : `SAVE ${yearly.savings_percentage}%`;

  return (
    <motion.div
      key="annual"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark text-white p-7 md:p-8 shadow-lg mb-4"
    >
      <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-white/[0.06] pointer-events-none" />
      <div className="absolute -bottom-20 -left-12 w-52 h-52 rounded-full bg-white/[0.04] pointer-events-none" />

      <div className="relative">
        <div className="flex items-center justify-between gap-3 mb-5">
          <span className="font-display font-semibold text-base text-white/95">
            Yearly
          </span>
          <span className="font-sans text-[11px] font-bold uppercase tracking-wider bg-cream text-text-primary px-3 py-1.5 rounded-full shadow-sm">
            {ribbonText}
          </span>
        </div>

        <div className="mb-6">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-semibold text-5xl md:text-6xl text-white leading-none">
              {heroMonthly}
            </span>
            <span className="font-sans text-base text-white/70">/ month</span>
          </div>
          <p className="font-sans text-xs text-white/70 mt-3">
            <span className="text-white/95">{yearTotal}</span>
            {discount && (
              <>
                {' '}
                <span className="line-through text-white/55">
                  {yearly.display_price}
                </span>
              </>
            )}
            {' for your first year · Billed annually'}
          </p>
        </div>

        <ul className="space-y-3 mb-7">
          {PREMIUM_FEATURES.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <Check
                size={18}
                className="text-white flex-shrink-0 mt-0.5"
              />
              <span className="font-sans text-sm md:text-base text-white/95">
                {feature}
              </span>
            </li>
          ))}
        </ul>

        <OnDarkButton
          label={isPremium ? 'You already have Premium' : 'Get Premium yearly'}
          onClick={onSelect}
          loading={loading}
          disabled={isPremium || otherLoading}
        />
      </div>
    </motion.div>
  );
}

interface MonthlyCardProps {
  monthly: PriceInfo;
  isPremium: boolean;
  loading: boolean;
  otherLoading: boolean;
  onSelect: () => void;
}

function MonthlyCard({
  monthly,
  isPremium,
  loading,
  otherLoading,
  onSelect,
}: MonthlyCardProps) {
  const discount = monthly.onboarding_discount;
  const priceShown = discount?.display_price ?? monthly.display_price;
  const periodLabel = monthlyPeriodLabel(discount);
  const isDisabled = loading || isPremium || otherLoading;

  return (
    <motion.div
      key="monthly"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
      className="rounded-2xl bg-card border border-border p-5 md:p-6 mb-8"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-semibold text-base text-text-primary mb-1.5">
            Monthly
          </div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-semibold text-2xl text-text-primary leading-none">
              {priceShown}
            </span>
            {discount && (
              <span className="font-sans text-sm text-text-muted line-through">
                {monthly.display_price}
              </span>
            )}
            <span className="font-sans text-sm text-text-secondary">
              {periodLabel}
            </span>
          </div>
          <p className="font-sans text-xs text-text-muted mt-1.5">
            {discount && discountIsTemporary(discount)
              ? `Then ${monthly.display_price}/month. Cancel anytime.`
              : 'Billed every month. Cancel anytime.'}
          </p>
        </div>

        <button
          type="button"
          onClick={onSelect}
          disabled={isDisabled}
          className={`inline-flex items-center justify-center gap-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary-dark font-sans font-semibold text-sm py-2.5 px-5 transition-colors cursor-pointer flex-shrink-0 ${
            isDisabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          aria-label="Choose monthly plan"
        >
          {loading ? <Spinner /> : 'Choose monthly'}
        </button>
      </div>
    </motion.div>
  );
}
